import { useQueryClient } from "@tanstack/react-query";
import { CLOSE_STAGE_UPDATED_SUBJECT } from "@common/types/close-nats";
import { closeQueryKeys } from "@api/close.api";
import { invalidateClientsCloseOverviewQueries } from "@api/workspace.api";
import { useNatsSubscription } from "@hooks/useNatsSubscription";
import { useScope } from "@state/scopeContext.lib";

/**
 * Subscribes to organization.close-stage-updated NATS events and invalidates
 * the closes list cache so that org/client-closes and /work views stay in sync
 * when close stage or finishedAt changes due to task updates.
 *
 * Best-effort / fire-and-forget — errors are logged but never surfaced.
 */
export function useCloseLiveUpdates() {
    const { organizationId } = useScope();
    const queryClient = useQueryClient();

    useNatsSubscription(
        organizationId
            ? `organizations.${organizationId}.notifications.${CLOSE_STAGE_UPDATED_SUBJECT}`
            : null,
        {
            onMessage: () => {
                if (!organizationId) {
                    return;
                }
                // Invalidate all closes list queries for this organization
                // (covers both workspace-scoped and unscoped variants).
                // organizationId is always defined here — subject is null otherwise.
                queryClient.invalidateQueries({
                    queryKey: closeQueryKeys.organization(organizationId),
                    exact: false,
                });
                invalidateClientsCloseOverviewQueries(queryClient);
            },
            onError: (error) => {
                // eslint-disable-next-line no-console
                console.error(
                    "useCloseLiveUpdates: failed to process NATS message",
                    error,
                );
            },
        },
    );
}
